import React, { useState } from "react";
import EventsFilter from "./EventsFilter";
import AnalyticsFilterMap from "./AnalyticsFilterMap";
import { EventsService } from "../services/EventsService";
import {
  EventFilterRequest,
  EventResponseDto,
} from "../services/Dtos/EventsDtos";
import showToast from "../helpers/notification";

const AnalyticsMap: React.FC = () => {
  const [events, setEvents] = useState<EventResponseDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [showAddressFrom, setShowAddressFrom] = useState<boolean>(false);
  const [zoomToCoordinates, setZoomToCoordinates] = useState<{
    lat: number;
    lng: number;
    zoom?: number;
  } | null>(null);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);

  const handleStateSelected = (stateCode: string, lat: number, lng: number) => {
    console.log(`[STATE] State selected: ${stateCode} at lat=${lat.toFixed(6)}, lng=${lng.toFixed(6)}`);
    setSelectedStateCode(stateCode);
    setZoomToCoordinates({ lat, lng, zoom: 7 });
  };

  const handleResetZoom = () => {
    console.log(`[ZOOM] Resetting zoom to default level 5`);
    setSelectedStateCode(null);
    setZoomToCoordinates(null);
  };

  const handleShowHeatmap = () => {
    console.log(`[HEATMAP] Toggling heatmap display`);
    setShowHeatmap(!showHeatmap);
  };

  const handleFilter = async (filters: EventFilterRequest) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    // Reset selectedStateCode - it will be set by handleStateSelected if a state was selected during search
    setSelectedStateCode(null);
    setZoomToCoordinates(null);
    // Track selected event type and reset checkbox if not DELIVERED
    setSelectedEventType(filters.eventType || null);
    if (filters.eventType !== "DELIVERED") {
      setShowAddressFrom(false);
    }

    try {
      const response = await EventsService.events.filter(filters);

      if (response.success) {
        setEvents(response.data || []);
        // showToast("success", `Successfully loaded ${response.data?.length || 0} event(s)`);
      } else {
        setError(response.message || "Failed to fetch events");
        showToast("error", response.message || "Failed to fetch events");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while fetching events. Please try again.";
      setError(errorMessage);
      showToast("error", errorMessage);
      console.error("Filter error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h1 className="text-primary fw-bold">Analytics Map</h1>
        <p className="text-muted">
          Filter and visualize delivery events on a map across your organization
        </p>
      </div>

      <EventsFilter onFilter={handleFilter} onStateSelected={handleStateSelected} onResetZoom={handleResetZoom} onShowHeatmap={handleShowHeatmap} loading={loading} />

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary me-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mb-0">Fetching events...</p>
        </div>
      )}

      {/* Show Address From Checkbox - Only available for DELIVERED events */}
      {!loading && hasSearched && events.length > 0 && selectedEventType === "DELIVERED" && (
        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="showAddressFromCheckbox"
              checked={showAddressFrom}
              onChange={(e) => setShowAddressFrom(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="showAddressFromCheckbox">
              Show Address From
            </label>
          </div>
        </div>
      )}

      {/* Analytics Filter Map - Always show in filter mode (empty by default, populated after search) */}
      <AnalyticsFilterMap
        events={events}
        showAddressFrom={showAddressFrom}
        zoomToCoordinates={zoomToCoordinates}
        selectedStateCode={selectedStateCode}
        showHeatmap={showHeatmap}
      />
    </div>
  );
};

export default AnalyticsMap;
